/**
 * Format a number with commas, returns NaN if the number cannot be converted
 * @param {string|number} num
 * @returns {number|string}
 */
export const formatNumber = (num) => {
	// eslint-disable-next-line no-param-reassign
	if (typeof num === 'string') num = Number(num);
	if ([null, undefined].includes(num) || !Number.isFinite(num)) return Number.NaN;
	if (Number.isInteger(num)) return num.toString().replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,');
	const [first, second] = num.toString().split('.');
	const parts = [first.replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,'), second];
	return parts.join('.');
};

export const formatNumberDollars = (num) => {
	if ([null, undefined].includes(num) || !Number.isFinite(num)) return Number.NaN;
	const formattedNum = formatNumber(num);
	if (num < 0) return `-$${formattedNum.slice(1)}`;
	return `$${formattedNum}`;
};

/**
 * Capitalize a single word
 * @param {string} word
 * @returns {string}
 */
export const capitalCase = (word) => {
	if (typeof word !== 'string') throw TypeError('Not a string');
	if (Number.isFinite(Number(word))) throw TypeError('String value was a number');
	return word[0].toUpperCase() + word.slice(1).toLowerCase();
};

/**
 * Check if a number is, in fact, a number
 * @param {string|number} val
 * @returns {boolean}
 */
export const isValidNumber = (val) => {
	if (typeof val === 'number') return Number.isFinite(val);
	if (typeof val === 'string') return Number.isFinite(parseFloat(val));
	return false;
};

/**
 * Reduce an array and return the sum of all its elements
 * @param {(number|string)[]} arr
 * @returns {number}
 */
export const sum = (arr) =>
	arr.reduce(
		/**
		 *
		 * @param {number} acc
		 * @param {number|string} cur
		 * @returns {number}
		 */
		(acc, cur) => {
			if (isValidNumber(cur)) return acc + parseFloat(cur);
			return acc;
		},
		0
	);

/**
 * Initialize an array and fill it using a fixed value or function
 * @param length
 * @param fill
 * @returns {any[]}
 */
export const arrayInit = (length, fill) => new Array(length).fill(fill);

/**
 * Handle classifier model updates
 * @param acquisitionDistributionFrac
 * @param acquisitionRate
 * @param averagePremium
 * @param carrierNonRenewalRate
 * @param pinpointLift
 * @param policiesInForce
 * @param riskCost
 * @param yearlyCancellationRate
 * @returns {{ baselinePremiums: *, baselinePremiumsSum: number, baselineRiskCosts: *, baselineRiskCostsSum: number, oneYearBaselinePifs, oneYearBaselinePifsSum: number, pinpointPifs, pinpointPifsSum: number, pinpointPremiums: *, pinpointPremiumsSum: number, pinpointRiskCosts: *, pinpointRiskCostsSum: number, baselineLessRiskCost: number, pinpointLessRiskCost: number, costSavings: number, costSavingsTriple: number, netGrowthRate: number, percentagePoints: number, renewalStrategy: number, renewalStrategyTriple: number, targetedGrowthStrategy: number, targetedGrowthStrategyTriple: number }}
 */
export function handleClassifierUpdate({
	acquisitionDistributionFrac,
	acquisitionRate,
	averagePremium,
	carrierNonRenewalRate,
	pinpointLift,
	policiesInForce,
	riskCost,
	yearlyCancellationRate,
}) {
	const buckets = { deciles: 10 };
	// NOTE: helpers for calculations
	const netGrowthRate = acquisitionRate - carrierNonRenewalRate - yearlyCancellationRate;
	const netGrowthFrac = netGrowthRate / 100;
	const cancellationFrac = yearlyCancellationRate / 100;
	const nonRenewalFrac = carrierNonRenewalRate / 100;
	const acquisitionFrac = acquisitionRate / 100;
	const yearlyNonRenewals = nonRenewalFrac * policiesInForce;
	const baselinePif = policiesInForce / buckets.deciles;
	const cancellationPif = baselinePif * cancellationFrac;
	const oneYearBaselinePif = baselinePif * (1 + netGrowthFrac);
	const baselinePremium = oneYearBaselinePif * averagePremium;
	const riskCostPerPolicy = riskCost / policiesInForce;
	const baselineRiskCost = oneYearBaselinePif * riskCostPerPolicy;
	const baselineLossPerPolicy = baselineRiskCost / oneYearBaselinePif;
	const oneYearBaselinePifs = Array(buckets.deciles).fill(oneYearBaselinePif);
	const baselinePremiums = Array(buckets.deciles).fill(baselinePremium);
	const pinpointDecilePifGrowth = acquisitionDistributionFrac.map((rate) => rate * policiesInForce * acquisitionFrac);
	const nonRenewalPifs = Array(buckets.deciles)
		.fill(null)
		.map((_, decile) => {
			// NOTE: calculate the test value
			const baselinePifsSliceSum = baselinePif * (buckets.deciles - decile);
			const cancellationPifsSliceSum = cancellationPif * (decile - buckets.deciles);
			const projectedBaselinePifsSum = cancellationPifsSliceSum + baselinePifsSliceSum;
			if (yearlyNonRenewals > projectedBaselinePifsSum) return -baselinePif + cancellationPif;
			return null; // NOTE: in this case, we return null and walk the array backwards
		})
		.reverse();
	nonRenewalPifs.forEach((elem, idx) => {
		if (nonRenewalPifs[idx] === null)
			nonRenewalPifs[idx] = -nonRenewalFrac * policiesInForce - sum(nonRenewalPifs.slice(0, idx));
	});
	nonRenewalPifs.reverse();
	const pinpointPifs = nonRenewalPifs.map(
		(nonRenewals, idx) => baselinePif - cancellationPif + nonRenewals + pinpointDecilePifGrowth[idx]
	);
	const pinpointPremiums = pinpointPifs.map((decilePifs) => decilePifs * averagePremium);
	const baselineRiskCosts = Array(buckets.deciles).fill(baselineRiskCost);
	const scratchRiskCosts = pinpointLift.map((lift) => lift * baselineRiskCost);
	const pinpointLossPerPolicy = scratchRiskCosts.map((cost) => cost / oneYearBaselinePif);
	const pinpointRiskCosts = pinpointLossPerPolicy.map((loss, idx) => loss * pinpointPifs[idx]);
	const baselineLossReductions = oneYearBaselinePifs.map(
		(_, idx) => baselineLossPerPolicy * (-cancellationPif + nonRenewalPifs[idx])
	);
	const pinpointLossReductions = pinpointLossPerPolicy.map(
		(loss, idx) => loss * (-cancellationPif + nonRenewalPifs[idx])
	);

	const baselineGrowthLossCosts = Array(buckets.deciles).fill(
		baselineLossPerPolicy * (baselinePif * (1 + acquisitionFrac))
	);
	const pinpointGrowthLossCosts = pinpointLossPerPolicy.map(
		(loss, idx) => loss * (pinpointDecilePifGrowth[idx] + baselinePif)
	);
	const baselineLossReductionSum = sum(baselineLossReductions);
	const pinpointLossReductionSum = sum(pinpointLossReductions);
	const renewalStrategy = -1 * pinpointLossReductionSum + baselineLossReductionSum;
	const baselineGrowthLossCostSum = sum(baselineGrowthLossCosts);
	const pinpointGrowthLossCostSum = sum(pinpointGrowthLossCosts);
	const targetedGrowthStrategy = -1 * pinpointGrowthLossCostSum + baselineGrowthLossCostSum;
	const oneYearBaselinePifsSum = sum(oneYearBaselinePifs);
	const baselineRiskCostsSum = sum(baselineRiskCosts);
	const baselinePremiumsSum = sum(baselinePremiums);
	const pinpointPifsSum = sum(pinpointPifs);
	const pinpointPremiumsSum = sum(pinpointPremiums);
	const pinpointRiskCostsSum = sum(pinpointRiskCosts);
	const baselineLessRiskCost = baselinePremiumsSum - baselineRiskCostsSum;
	const pinpointLessRiskCost = pinpointPremiumsSum - pinpointRiskCostsSum;
	const costSavings = pinpointLessRiskCost - baselineLessRiskCost;
	const percentagePoints =
		100 * (baselineRiskCostsSum / baselinePremiumsSum - pinpointRiskCostsSum / pinpointPremiumsSum);
	return {
		baselinePremiums,
		baselinePremiumsSum,
		baselineRiskCosts,
		baselineRiskCostsSum,
		oneYearBaselinePifs,
		oneYearBaselinePifsSum,
		pinpointPifs,
		pinpointPifsSum,
		pinpointPremiums,
		pinpointPremiumsSum,
		pinpointRiskCosts,
		pinpointRiskCostsSum,
		baselineLessRiskCost,
		pinpointLessRiskCost,
		costSavings,
		costSavingsTriple: 3 * costSavings,
		netGrowthRate,
		percentagePoints,
		renewalStrategy,
		renewalStrategyTriple: 3 * renewalStrategy,
		targetedGrowthStrategy,
		targetedGrowthStrategyTriple: 3 * targetedGrowthStrategy,
	};
}

/**
 * Handle actuarial model updates
 * @param acqDstFrc
 * @param newBizRt
 * @param avgPrm
 * @param nonRnwRt
 * @param cltRskCst
 * @param cltPrms
 * @param cltPIFFrc
 * @param PIF
 * @param pptRskCst
 * @param pptPrms
 * @param pptPIFFrc
 * @param rskCst
 * @param cnclRt
 * @param LYCltPIF
 * @param LYCltRskCst
 * @param LYCltPrms
 * @param LYPptPIF
 * @param LYPptRskCst
 * @param LYPptPrms
 * @returns {ActuarialCalculatorOutput}
 */
export function handleActuarialUpdate({
	riskCost: rskCst, // v9_AT_9-II-24 C4
	policiesInForce: PIF, // v9_AT_9-II-24 C5
	averagePremium: avgPrm, // v9_AT_9-II-24 C6
	yearlyCancellationRate: cnclRt, // v9_AT_9-II-24 C8
	carrierNonRenewalRate: nonRnwRt, // v9_AT_9-II-24 C9
	acquisitionRate: newBizRt, // v9_AT_9-II-24 C10
	acquisitionDistributionFrac: acqDstFrc, // v9_AT_9-II-24 H/AN
	clientPIFMeanFracs: cltPIFFrc, // v9_AT_9-II-24 M
	clientMeanLosses: cltRskCst, // v9_AT_9-II-24 N
	clientMeanPremiums: cltPrms, // v9_AT_9-II-24 O
	pptPIFMeanFracs: pptPIFFrc, // v9_AT_9-II-24 AD
	pptMeanLosses: pptRskCst, // v9_AT_9-II-24 AE
	pptMeanPremiums: pptPrms, // v9_AT_9-II-24 AF
	lastYearClientPIF: LYCltPIF, // v9_AT_9-II-24 M
	lastYearClientRiskCosts: LYCltRskCst, // v9_AT_9-II-24 N
	lastYearClientPremiums: LYCltPrms, // v9_AT_9-II-24 O
	lastYearPptPIF: LYPptPIF, // v9_AT_9-II-24 AD
	lastYearPptRiskCosts: LYPptRskCst, // v9_AT_9-II-24 AE
	lastYearPptPremiums: LYPptPrms, // v9_AT_9-II-24 AF
}) {
	// NOTE: HELPERS
	const acqFrc = newBizRt / 100; // v9_AT_9-II-24 C10
	const netGrwRt = newBizRt - nonRnwRt - cnclRt; // v9_AT_9-II-24 C10 - C9 - C8
	const netGrwFrc = netGrwRt / 100; // v9_AT_9-II-24 C10 - C9 - C8
	const nonRnwFrc = nonRnwRt / 100; // v9_AT_9-II-24 C9
	const nonRnwPIF = PIF * nonRnwFrc; // v9_AT_9-II-24 C5 * C9
	const negNonRnwPIF = -PIF * nonRnwFrc; // v9_AT_9-II-24 -C5 * C9
	const cnclFrc = cnclRt / 100; // v9_AT_9-II-24 C8
	// const PIFLssFrc = cnclFrc + nonRnwFrc; // v9_AT_9-II-24 C8 + C9
	const smPptRskCst = sum(pptRskCst); // v9_AT_9-II-24 AD31
	const smPptPrms = sum(pptPrms); // v9_AT_9-II-24 AF31
	const smCltRskCst = sum(cltRskCst); // v9_AT_9-II-24 N31
	const smCltPrms = sum(cltPrms); // v9_AT_9-II-24 O31

	const cltStPIF = LYCltPIF ?? cltPIFFrc.map((frc) => frc * PIF); // v9_AT_9-II-24 P
	const cltStRskCst = LYCltRskCst ?? cltRskCst.map((cst) => (cst / smCltRskCst) * rskCst); // v9_AT_9-II-24 Q
	// const smCltStRskCst = sum(cltStRskCst); // v9_AT_9-II-24 Q31
	const cltStPrms = LYCltPrms ?? cltPrms.map((prm) => (prm / smCltPrms) * (PIF * avgPrm)); // v9_AT_9-II-24 R
	// const smCltStPrms = sum(cltStPrms); // v9_AT_9-II-24 R31
	const cltLssPP = cltStRskCst.map((cst, idx) => cst / cltStPIF[idx]); // v9_AT_9-II-24 S
	const cltPrmPP = cltStPrms.map((prm, idx) => prm / cltStPIF[idx]); // v9_AT_9-II-24 T
	const clt1yPIF = cltStPIF.map((pcs) => pcs * (1 + netGrwFrc)); // v9_AT_9-II-24 U
	const smClt1yPIF = sum(clt1yPIF); // v9_AT_9-II-24 U31/D31
	const clt1yRskCst = cltLssPP.map((lss, idx) => lss * clt1yPIF[idx]); // v9_AT_9-II-24 V
	const smClt1yRskCst = sum(clt1yRskCst); // v9_AT_9-II-24 V31/E31
	const cltCnclRskCst = cltLssPP.map((lss, idx) => cnclFrc * -lss * cltStPIF[idx]); // v9_AT_9-II-24 W
	const smCltCnclRskCst = sum(cltCnclRskCst); // v9_AT_9-II-24 W31
	const cltNonRnwRskCst = cltLssPP.map((lss, idx) => nonRnwFrc * -lss * cltStPIF[idx]); // v9_AT_9-II-24 X
	const smCltNonRnwRskCst = sum(cltNonRnwRskCst); // v9_AT_9-II-24 X31
	const cltGrwRskCst = cltLssPP.map((lss, idx) => acqFrc * lss * cltStPIF[idx]); // v9_AT_9-II-24 Y
	const smCltGrwRskCst = sum(cltGrwRskCst); // v9_AT_9-II-24 Y31
	const clt1yPrms = cltPrmPP.map((prm, idx) => prm * clt1yPIF[idx]); // v9_AT_9-II-24 Z
	const smClt1yPrms = sum(clt1yPrms); // v9_AT_9-II-24 Z31/F31
	const cltCnclPrms = cltPrmPP.map((prm, idx) => prm * -cnclFrc * cltStPIF[idx]); // v9_AT_9-II-24 AA
	const smCltCnclPrms = sum(cltCnclPrms); // v9_AT_9-II-24 AA31
	const cltNonRnwPrms = cltPrmPP.map((prm, idx) => prm * -nonRnwFrc * cltStPIF[idx]); // v9_AT_9-II-24 AB
	const smCltNonRnwPrms = sum(cltNonRnwPrms); // v9_AT_9-II-24 AB31
	const cltGrwPrms = cltPrmPP.map((prm, idx) => acqFrc * prm * cltStPIF[idx]); // v9_AT_9-II-24 AC
	const smCltGrwPrms = sum(cltGrwPrms); // v9_AT_9-II-24 AC31

	const pptStPIF = LYPptPIF ?? pptPIFFrc.map((frac) => frac * PIF); // v9_AT_9-II-24 AG
	const pptStRskCst = LYPptRskCst ?? pptRskCst.map((cst) => (cst / smPptRskCst) * rskCst); // v9_AT_9-II-24 AH
	// const smPptStRskCst = sum(pptStRskCst); // v9_AT_9-II-24 AH31
	const pptStPrms = LYPptPrms ?? pptPrms.map((prm) => (prm / smPptPrms) * PIF * avgPrm); // v9_AT_9-II-24 AI
	// const smPptStPrms = sum(pptStPrms);  // v9_AT_9-II-24 AI31
	const pptLssPP = pptStRskCst.map((lss, idx) => lss / pptStPIF[idx]); // v9_AT_9-II-24 AJ
	const pptPrmPP = pptStPrms.map((prm, idx) => prm / pptStPIF[idx]); // v9_AT_9-II-24 AK
	const pptCnclPIF = pptStPIF.map((pcs) => pcs * cnclFrc * -1); // v9_AT_9-II-24 AL
	const pptNonRnwPIF = Array(10).fill(null); // v9_AT_9-II-24 AM
	for (let idx = 9; idx >= 0; idx -= 1) {
		const newPIF = sum(pptStPIF.slice(idx));
		const cancelledPIF = sum(pptCnclPIF.slice(idx));
		const nonRnwPIFGtGrwPIF = nonRnwPIF > newPIF + cancelledPIF;
		if (nonRnwPIFGtGrwPIF) pptNonRnwPIF[idx] = -pptStPIF[idx] - pptCnclPIF[idx];
		else pptNonRnwPIF[idx] = negNonRnwPIF - sum(pptNonRnwPIF.slice(idx + 1));
	}
	const pptGrwPIF = acqDstFrc.map((frc) => frc * acqFrc * PIF); // v9_AT_9-II-24 AO
	const ppt1yPIF = pptStPIF.map((pcs, idx) => pcs + pptCnclPIF[idx] + pptNonRnwPIF[idx] + pptGrwPIF[idx]); // v9_AT_9-II-24 AP
	const smPpt1yPIF = sum(ppt1yPIF); // v9_AT_9-II-24 AP31/I31
	const ppt1yRskCst = pptLssPP.map((lss, idx) => lss * ppt1yPIF[idx]); // v9_AT_9-II-24 AQ
	const smPpt1yRskCst = sum(ppt1yRskCst); // v9_AT_9-II-24 AQ31/J31
	const pptCnclRskCst = pptLssPP.map((lss, idx) => lss * pptCnclPIF[idx]); // v9_AT_9-II-24 AR
	const smPptCnclRskCst = sum(pptCnclRskCst); // v9_AT_9-II-24 AR31
	const pptNonRnwRskCst = pptLssPP.map((lss, idx) => lss * pptNonRnwPIF[idx]); // v9_AT_9-II-24 AS
	const smPptNonRnwRskCst = sum(pptNonRnwRskCst); // v9_AT_9-II-24 AS31
	const pptGrwRskCst = pptLssPP.map((lss, idx) => lss * pptGrwPIF[idx]); // v9_AT_9-II-24 AT
	const smPptGrwRskCst = sum(pptGrwRskCst); // v9_AT_9-II-24 AT31
	const ppt1yPrms = pptPrmPP.map((prm, idx) => prm * ppt1yPIF[idx]); // v9_AT_9-II-24 AU
	const smPpt1yPrms = sum(ppt1yPrms); // v9_AT_9-II-24 AU31/K31
	const pptCnclPrms = pptPrmPP.map((prm, idx) => prm * pptCnclPIF[idx]); // v9_AT_9-II-24 AV
	const smPptCnclPrms = sum(pptCnclPrms); // v9_AT_9-II-24 AV31
	const pptNonRnwPrms = pptPrmPP.map((prm, idx) => prm * pptNonRnwPIF[idx]); // v9_AT_9-II-24 AW
	const smPptNonRnwPrms = sum(pptNonRnwPrms); // v9_AT_9-II-24 AW31
	const pptGrwPrms = pptPrmPP.map((prm, idx) => prm * pptGrwPIF[idx]); // v9_AT_9-II-24 AX
	const smPptGrwPrms = sum(pptGrwPrms); // v9_AT_9-II-24 AX31
	// const pptStPrf = smPptStPrms - smPptStRskCst; // v9_AT_9-II-24 AT47 AG31 - AF31
	// const cltStPrf = smCltStPrms - smCltStRskCst; // v9_AT_9-II-24 AT48 O31 - N31
	const pptGrwPrf = smPptGrwPrms - smPptGrwRskCst; // v9_AT_9-II-24 AX36 AX31 - AT31
	const pptCnclPrf = smPptCnclPrms - smPptCnclRskCst; // v9_AT_9-II-24 AX37 AV31 - AR31
	const pptNonRnwPrf = smPptNonRnwPrms - smPptNonRnwRskCst; // v9_AT_9-II-24 AX38 AW31 - AS31
	const cltGrwPrf = smCltGrwPrms - smCltGrwRskCst; // v9_AT_9-II-24 AX40 AC31 - Y31
	const cltCnclPrf = smCltCnclPrms - smCltCnclRskCst; // v9_AT_9-II-24 AX41 AA31 - W31
	const cltNonRnwPrf = smCltNonRnwPrms - smCltNonRnwRskCst; // v9_AT_9-II-24 AX42 AB31 - X31
	const grw1yROI = pptGrwPrf - cltGrwPrf; // v9_AT_9-II-24 AX44 AX36 - AX40
	const cncl1yROI = pptCnclPrf - cltCnclPrf; // v9_AT_9-II-24 AX45 AX37 - AX41
	const nonRnw1yROI = pptNonRnwPrf - cltNonRnwPrf; // v9_AT_9-II-24 AX46 AX38 - AX42
	const ttlROI = grw1yROI + cncl1yROI + nonRnw1yROI; // v9_AT_9-II-24 AX47 AX44 + AX45 + AX46

	const cltPrf = smClt1yPrms - smClt1yRskCst; // v9_AT_9-II-24 F33/C13 F31 - E31
	const pptPrf = smPpt1yPrms - smPpt1yRskCst; // v9_AT_9-II-24 K34/C14 K31 - J31
	const clt1ySv = pptPrf - cltPrf; // v9_AT_9-II-24 C15 K34 - F33
	const ptsLssRtR = 100 * (smClt1yRskCst / smClt1yPrms - smPpt1yRskCst / smPpt1yPrms); // v9_AT_9-II-24 G15 100 * (V31/Y31 - AO31/AR31)
	return {
		baselinePremiums: clt1yPrms, // Baseline Premiums
		baselinePremiumsSum: smClt1yPrms, // Baseline Premiums Sum
		baselineRiskCosts: clt1yRskCst, // Baseline Risk Cost
		baselineRiskCostsSum: smClt1yRskCst, // Baseline Risk Cost Sum
		oneYearBaselinePifs: clt1yPIF, // Baseline PIF in 1 Year
		oneYearBaselinePifsSum: smClt1yPIF, // Baseline PIF in 1 Year Sum
		pinpointPifs: ppt1yPIF, // PIF with Pinpoint Growth
		pinpointPifsSum: smPpt1yPIF, // PIF with Pinpoint Growth Sum
		pinpointPremiums: ppt1yPrms, // Pinpoint Premiums
		pinpointPremiumsSum: smPpt1yPrms, // Pinpoint Premiums Sum
		pinpointRiskCosts: ppt1yRskCst, // Pinpoint Risk Cost
		pinpointRiskCostsSum: smPpt1yRskCst, // Pinpoint Risk Cost Sum
		baselineLessRiskCost: cltPrf, // Baseline Px Less Risk Cost
		pinpointLessRiskCost: pptPrf, // Pinpoint Px Less Risk Cost
		costSavings: clt1ySv, // 1-year Client Savings
		netGrowthRate: netGrwRt, // Net Growth Rate
		cancellationRoI: cncl1yROI, // Cancellation Related RoI
		nonRenewalRoI: nonRnw1yROI, // Non-renewal Related RoI
		newBusinessRoI: grw1yROI, // Growth Related RoI
		totalRoI: ttlROI, // Total ROI
		lossRatioReduction: ptsLssRtR, // Loss Ratio Reduction Points
	};
}

/**
 * Actuarial calculator output
 * @typedef {Object} ActuarialCalculatorOutput
 * @property {number[]} baselinePremiums
 * @property {number} baselinePremiumsSum
 * @property {number[]} baselineRiskCosts
 * @property {number} baselineRiskCostsSum
 * @property {number[]} oneYearBaselinePifs
 * @property {number} oneYearBaselinePifsSum
 * @property {number[]} pinpointPifs
 * @property {number} pinpointPifsSum
 * @property {number[]} pinpointPremiums
 * @property {number} pinpointPremiumsSum
 * @property {number[]} pinpointRiskCosts
 * @property {number} pinpointRiskCostsSum
 * @property {number} baselineLessRiskCost
 * @property {number} pinpointLessRiskCost
 * @property {number} costSavings
 * @property {number|undefined} netGrowthRate
 * @property {number|undefined} cancellationRoI
 * @property {number|undefined} nonRenewalRoI
 * @property {number|undefined} newBusinessRoI
 * @property {number|undefined} totalRoI
 * @property {number|undefined} lossRatioReduction
 * @property {number|undefined} costSavingsTriple
 * @property {number|undefined} percentagePoints
 * @property {number|undefined} renewalStrategy
 * @property {number|undefined} renewalStrategyTriple
 * @property {number|undefined} targetedGrowthStrategy
 * @property {number|undefined} targetedGrowthStrategyTriple
 */
